import React, { useEffect, useCallback, useState } from 'react';
import maplibregl from 'maplibre-gl';
import config from '../../config';
import { addMVTLayers } from '../../utils/mapLayers';
import { usePilot3D } from '../../hooks/usePilot3D';
import { PilotData } from '../../types';

interface Map3DTerrainProps {
  map: maplibregl.Map | null;
  enabled: boolean;
  exaggeration?: number;
  hillshadeIntensity?: number; // Deprecated - hillshade is controlled by style.json
  showLabels?: boolean;
  onLoadingStateChange?: (isLoading: boolean) => void;
  raceId?: string;
  trackManager?: any; // TrackManager instance for ensuring tracks persist
  pilots?: Map<string, PilotData>; // Pilots data for 3D visualization
}

interface TerrainSettings {
  exaggeration: number;
  hillshadeIntensity: number;
  showLabels: boolean;
}

const Map3DTerrain: React.FC<Map3DTerrainProps> = ({
  map,
  enabled,
  exaggeration = 1.5,
  hillshadeIntensity = 0.5, // Deprecated - kept for compatibility
  showLabels = true,
  onLoadingStateChange,
  raceId,
  trackManager,
  pilots = new Map()
}) => {
  const [settings, setSettings] = useState<TerrainSettings>({
    exaggeration,
    hillshadeIntensity,
    showLabels
  });

  const [isTerrainLoaded, setIsTerrainLoaded] = useState(false);
  const [isTerrainLoading, setIsTerrainLoading] = useState(false);

  // Use the 3D pilot hook
  usePilot3D({
    map,
    pilots,
    enabled,
    is3D: enabled // 3D pilots only when terrain is enabled
  });

  // Add terrain to existing style - following example.html pattern
  const addTerrainToCurrentStyle = useCallback(() => {
    if (!map) return;

    try {
      // Check if terrain source already exists
      const existingTerrain = map.getSource('terrain');

      // Remove if it exists but is wrong type
      if (existingTerrain && (existingTerrain as any).type !== 'raster-dem') {
        map.removeSource('terrain');
      }

      // Add terrain source if it doesn't exist or was removed
      if (!map.getSource('terrain')) {
        const TILE_SERVER_URL = config.map.tileServerUrl;

        // Use the same URL pattern as example.html
        // Add cache buster temporarily until Cloudflare cache expires
        const cacheBuster = new Date().getTime();
        const terrainTiles = TILE_SERVER_URL.includes('terrain.hikeandfly.app')
          ? [`${TILE_SERVER_URL}/terrain/terrarium/{z}/{x}/{y}.png?v=${cacheBuster}`]
          : [`/terrain/terrarium/{z}/{x}/{y}.png`];

        map.addSource('terrain', {
          type: 'raster-dem',
          encoding: 'terrarium',
          tiles: terrainTiles,
          tileSize: 256,
          minzoom: 1,
          maxzoom: 15
        });
      }

      // Set terrain with exaggeration
      map.setTerrain({
        source: 'terrain',
        exaggeration: settings.exaggeration
      });
    } catch (error) {
      console.error('Error adding terrain:', error);
      // Try fallback without terrain if it fails
    }
  }, [map, settings.exaggeration]);

  const enableTerrain = useCallback(() => {
    if (!map || !enabled || isTerrainLoading || isTerrainLoaded) {
      // Skipping enableTerrain
      return;
    }

    // Starting terrain enable process
    setIsTerrainLoading(true);
    onLoadingStateChange?.(true);

    // Clean up any custom layers before changing style to prevent errors
    const cleanupCustomLayers = () => {
      const style = map.getStyle();
      if (!style) return;

      // Stop all animations and pending operations
      map.stop();

      // Remove all symbol layers first (text layers that might be causing issues)
      if (style.layers) {
        const symbolLayers = style.layers.filter(layer =>
          layer.type === 'symbol' && (
            layer.id.includes('waypoint') ||
            layer.id.includes('task') ||
            layer.id.includes('label')
          )
        );

        symbolLayers.forEach(layer => {
          try {
            map.removeLayer(layer.id);
          } catch (e) {
            // Ignore
          }
        });
      }

      // Then collect and remove all other custom layers and sources
      const layersToRemove: string[] = [];
      const sourcesToRemove: string[] = [];

      // Find all custom layers
      if (style.layers) {
        style.layers.forEach(layer => {
          if (layer.id.startsWith('waypoint-') ||
              layer.id.startsWith('task-') ||
              layer.id.includes('waypoint') ||
              layer.id.includes('task')) {
            layersToRemove.push(layer.id);
          }
        });
      }

      // Find all custom sources
      if (style.sources) {
        Object.keys(style.sources).forEach(sourceId => {
          if (sourceId.startsWith('waypoint-') ||
              sourceId.startsWith('task-') ||
              sourceId.includes('waypoint') ||
              sourceId.includes('task')) {
            sourcesToRemove.push(sourceId);
          }
        });
      }

      // Remove remaining layers
      layersToRemove.forEach(layerId => {
        try {
          if (map.getLayer(layerId)) {
            map.removeLayer(layerId);
          }
        } catch (e) {
          // Ignore
        }
      });

      // Remove sources
      sourcesToRemove.forEach(sourceId => {
        try {
          if (map.getSource(sourceId)) {
            map.removeSource(sourceId);
          }
        } catch (e) {
          // Ignore
        }
      });
    };

    // Track if style has loaded (moved outside promise scope)
    let styleLoadComplete = false;
    let loadingTimeout: NodeJS.Timeout | undefined;

    try {
      // Add error handler for terrain loading issues
      const handleError = (e: any) => {
        if (e.error && e.error.status === 404) {
          // Terrain tiles not found, continuing without 3D
          // Continue without terrain but still enable 3D view
          setIsTerrainLoaded(true);
          setIsTerrainLoading(false);
          onLoadingStateChange?.(false);
          styleLoadComplete = true;
          if (loadingTimeout) clearTimeout(loadingTimeout);
        }
      };

      map.once('error', handleError);

      // Check if we should load the full style.json (like example.html does)
      const TILE_SERVER_URL = config.map.tileServerUrl;
      const styleUrl = `${TILE_SERVER_URL}/api/style.json`;

      // Try to load the full style for rich 3D experience
      // Fetching style

      fetch(styleUrl)
        .then(response => {
          // Style fetched
          if (response.ok) {
            return response.json();
          }
          throw new Error('Style not available');
        })
        .then(styleJson => {
          // Processing style JSON

          // Track if we've already handled the style ready
          let styleHandled = false;

          // Only set timeout once we know we're processing the style
          loadingTimeout = setTimeout(() => {
            if (!styleLoadComplete) {
              // Style load timeout
              // Force handle the style if it hasn't been handled yet
              if (!styleHandled && map.isStyleLoaded()) {
                // Forcing style ready
                // Call onStyleReady directly since handleStyleReady isn't defined yet
                onStyleReady();
              } else {
                onLoadingStateChange?.(false);
                setIsTerrainLoaded(true);
                setIsTerrainLoading(false);
              }
            }
          }, 3000);  // 3 seconds from when style processing starts

          // Store current view state and style for restoration
          const currentCenter = map.getCenter();
          const currentZoom = map.getZoom();

          // Process the style to fix relative URLs
          const processedStyle = JSON.parse(JSON.stringify(styleJson));

          // Fix all source URLs to use full terrain server URL
          Object.keys(processedStyle.sources).forEach(sourceId => {
            const source = processedStyle.sources[sourceId];

            // Fix GeoJSON data URLs
            if (source.type === 'geojson' && source.data && typeof source.data === 'string') {
              if (source.data.startsWith('/data/')) {
                source.data = `${TILE_SERVER_URL}${source.data}`;
              }
            }

            // Fix raster tile URLs
            if (source.tiles) {
              source.tiles = source.tiles.map((tile: string) => {
                if (tile.startsWith('/')) {
                  return `${TILE_SERVER_URL}${tile}`;
                }
                return tile;
              });
            }

            // Fix raster-dem tile URLs (terrain)
            if (source.type === 'raster-dem' && source.tiles) {
              source.tiles = source.tiles.map((tile: string) => {
                if (tile.startsWith('/terrain/')) {
                  // Add cache buster for terrain tiles
                  const cacheBuster = new Date().getTime();
                  return `${TILE_SERVER_URL}${tile}?v=${cacheBuster}`;
                }
                return tile;
              });
            }
          });

          // Define the style ready handler first
          const onStyleReady = () => {
            // Configuring terrain

            // Mark style as loaded
            styleLoadComplete = true;

            // Clear the timeout since style loaded successfully
            clearTimeout(loadingTimeout);

            // Ensure terrain is configured
            addTerrainToCurrentStyle();

            // Hide labels initially (as per showLabels prop which defaults to false)
            // Using direct implementation instead of toggleLabels to avoid circular dependency
            if (!showLabels) {
              const visibility = 'none';
              const labelLayers = ['city-labels', 'peak-labels', 'water-labels', 'park-labels'];

              const layers = map.getStyle()?.layers;
              if (layers) {
                layers.forEach(layer => {
                  if (labelLayers.includes(layer.id) ||
                      layer.id.includes('label') ||
                      layer.id.includes('text') ||
                      layer.id.includes('place') ||
                      layer.id.includes('poi')) {
                    try {
                      map.setLayoutProperty(layer.id, 'visibility', visibility);
                    } catch (e) {
                      // Silently ignore if layer doesn't exist
                    }
                  }
                });
              }
            }

            // Fog is now defined in style.json

            // Restore view and animate to 3D
            map.jumpTo({
              center: currentCenter,
              zoom: currentZoom
            });

            map.easeTo({
              pitch: 60,
              bearing: -20,
              duration: 1500
            });

            setIsTerrainLoaded(true);
            setIsTerrainLoading(false);

            // Re-add MVT layers after style change
            if (raceId) {
              addMVTLayers(map, raceId);
            }

            // Fire style event to notify other components (like TrackManager) to re-add their layers
            // Wait a bit longer to ensure the style is fully ready
            setTimeout(() => {
              if (map.isStyleLoaded()) {
                map.fire('style.load');
                // Also trigger repaint to ensure 3D layers render
                setTimeout(() => {
                  map.triggerRepaint();
                }, 100);
              } else {
                // If style not loaded yet, wait for it
                map.once('idle', () => {
                  map.fire('style.load');
                  // Also trigger repaint to ensure 3D layers render
                  setTimeout(() => {
                    map.triggerRepaint();
                  }, 100);
                });
              }
            }, 500);

            // Stop loading immediately after animation starts
            onLoadingStateChange?.(false);
            map.off('error', handleError);
          };

          // Set up the event listener BEFORE setting the style
          // Setting up style listeners

          const handleStyleReady = () => {
            if (!styleHandled) {
              styleHandled = true;
              // Style ready
              onStyleReady();
            }
          };

          // Set up event listeners after the delay to match when style is set
          requestAnimationFrame(() => {
            setTimeout(() => {
              // Listen for multiple possible events
              map.once('style.load', handleStyleReady);
              map.once('idle', () => {
                // Backup: if style.load doesn't fire, idle means map is ready
                if (!styleHandled && map.isStyleLoaded()) {
                  // Style ready via idle
                  handleStyleReady();
                }
              });
            }, 200); // Match the setStyle timing
          });

          // Clean up custom layers before applying new style
          cleanupCustomLayers();

          // Wait for next frame and ensure cleanup is complete
          requestAnimationFrame(() => {
            setTimeout(() => {
              // Apply the processed style from API (includes hillshade, labels, etc.)
              // Setting processed style
              map.setStyle(processedStyle);
            }, 200); // Increased delay to ensure cleanup
          });

          // Check if the style is already loaded (rare but possible)
          setTimeout(() => {
            if (!styleHandled && map.isStyleLoaded()) {
              // Style loaded
              handleStyleReady();
            }
          }, 400); // Adjusted timing to account for all delays
        })
        .catch(error => {
          console.error('Style fetch failed:', error);

          // Mark as complete and clear timeout
          styleLoadComplete = true;
          clearTimeout(loadingTimeout);

          // Fallback: just add terrain to current style
          addTerrainToCurrentStyle();

          // Hide labels initially (as per showLabels prop which defaults to false)
          // Using direct implementation instead of toggleLabels to avoid circular dependency
          if (!showLabels) {
            const visibility = 'none';
            const labelLayers = ['city-labels', 'peak-labels', 'water-labels', 'park-labels'];

            const layers = map.getStyle()?.layers;
            if (layers) {
              layers.forEach(layer => {
                if (labelLayers.includes(layer.id) ||
                    layer.id.includes('label') ||
                    layer.id.includes('text') ||
                    layer.id.includes('place') ||
                    layer.id.includes('poi')) {
                  try {
                    map.setLayoutProperty(layer.id, 'visibility', visibility);
                  } catch (e) {
                    // Silently ignore if layer doesn't exist
                  }
                }
              });
            }
          }

          // Fog should be defined in style.json

          // Animate to 3D view
          map.easeTo({
            pitch: 60,
            bearing: -20,
            duration: 1500
          });

          setIsTerrainLoaded(true);
          setIsTerrainLoading(false);

          // Fire style event to notify other components to re-add their layers
          // Wait a bit longer to ensure the style is fully ready
          setTimeout(() => {
            if (map.isStyleLoaded()) {
              map.fire('style.load');
              // Also trigger repaint to ensure 3D layers render
              setTimeout(() => {
                map.triggerRepaint();
              }, 100);
            } else {
              // If style not loaded yet, wait for it
              map.once('idle', () => {
                map.fire('style.load');
                // Also trigger repaint to ensure 3D layers render
                setTimeout(() => {
                  map.triggerRepaint();
                }, 100);
              });
            }
          }, 500);

          // Stop loading immediately
          onLoadingStateChange?.(false);
          map.off('error', handleError);
        });
    } catch (error) {
      console.error('Error enabling terrain:', error);

      // Mark as complete and clear timeout
      styleLoadComplete = true;
      if (loadingTimeout) clearTimeout(loadingTimeout);

      // Still enable 3D view even if terrain fails
      map.easeTo({
        pitch: 60,
        bearing: -20,
        duration: 1500
      });
      setIsTerrainLoaded(true);
      setIsTerrainLoading(false);
      // Stop loading immediately
      onLoadingStateChange?.(false);
    }
  }, [map, enabled, addTerrainToCurrentStyle, onLoadingStateChange, showLabels, isTerrainLoading, isTerrainLoaded, raceId]);

  // Toggle labels visibility - defined before disableTerrain which uses it
  const toggleLabels = useCallback((show: boolean) => {
    if (!map) return;

    setSettings(prev => ({ ...prev, showLabels: show }));

    try {
      const visibility = show ? 'visible' : 'none';
      const labelLayers = ['city-labels', 'peak-labels', 'water-labels', 'park-labels'];

      // Also check for common label patterns
      const layers = map.getStyle()?.layers;
      if (layers) {
        layers.forEach(layer => {
          if (labelLayers.includes(layer.id) ||
              layer.id.includes('label') ||
              layer.id.includes('text') ||
              layer.id.includes('place') ||
              layer.id.includes('poi')) {
            try {
              map.setLayoutProperty(layer.id, 'visibility', visibility);
            } catch (e) {
              // Silently ignore if layer doesn't exist
            }
          }
        });
      }
    } catch (e) {
      // Error toggling labels
    }
  }, [map]);

  const disableTerrain = useCallback(() => {
    if (!map) return;

    // Disabling 3D terrain
    // Don't show loading spinner when going back to 2D
    // onLoadingStateChange?.(true);

    // First restore labels visibility before switching styles
    if (settings.showLabels === false) {
      toggleLabels(true); // Restore labels to visible
    }

    // Animate back to 2D view
    map.easeTo({
      pitch: 0,
      bearing: 0,
      duration: 1000
    });

    setTimeout(() => {
      try {
        // Remove terrain
        map.setTerrain(undefined as any);

        // Fog will be removed with style change

        // Clear any stored original style
        setIsTerrainLoaded(false);

        // Force switch back to OSM style
        const osmStyle = {
          version: 8,
          sources: {
            'osm-tiles': {
              type: 'raster',
              tiles: [
                'https://a.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://b.tile.openstreetmap.org/{z}/{x}/{y}.png',
                'https://c.tile.openstreetmap.org/{z}/{x}/{y}.png'
              ],
              tileSize: 256,
              attribution: '© OpenStreetMap contributors'
            }
          },
          layers: [{
            id: 'osm-tiles-layer',
            type: 'raster',
            source: 'osm-tiles',
            minzoom: 0,
            maxzoom: 19
          }]
        };

        map.setStyle(osmStyle as any);

        // Re-add MVT layers after style change
        map.once('style.load', () => {
          // 2D style loaded
          if (raceId) {
            addMVTLayers(map, raceId);
          }

          // IMPORTANT: Ensure tracks are restored after 3D->2D transition
          setTimeout(() => {
            console.log('🔧 Restoring tracks after 3D exit');

            // First fire the style.load event for any listeners
            map.fire('style.load');

            // Then directly call TrackManager if available
            if (trackManager) {
              console.log('📊 Directly calling TrackManager.onStyleChange()');
              trackManager.onStyleChange();
            }
          }, 200);
        });
      } catch (e) {
        console.log('Error removing terrain:', e);
        setIsTerrainLoaded(false);
      }
    }, 1000);
  }, [map, settings.showLabels, toggleLabels, raceId, trackManager]);

  // Update terrain exaggeration
  const updateExaggeration = useCallback((value: number) => {
    if (!map || !isTerrainLoaded) return;

    setSettings(prev => ({ ...prev, exaggeration: value }));
    try {
      map.setTerrain({
        source: 'terrain',
        exaggeration: value
      });
    } catch (e) {
      console.error('Error updating terrain exaggeration:', e);
    }
  }, [map, isTerrainLoaded]);


  // Handle enabling/disabling
  useEffect(() => {
    if (enabled && !isTerrainLoaded) {
      enableTerrain();
    } else if (!enabled && isTerrainLoaded) {
      disableTerrain();
    }
  }, [enabled, isTerrainLoaded, enableTerrain, disableTerrain]);

  // Handle exaggeration changes from props
  useEffect(() => {
    if (isTerrainLoaded && exaggeration !== settings.exaggeration) {
      updateExaggeration(exaggeration);
    }
  }, [exaggeration, settings.exaggeration, isTerrainLoaded, updateExaggeration]);

  // Hillshade intensity is now controlled by style.json - this effect is no longer needed
  useEffect(() => {
    if (hillshadeIntensity !== settings.hillshadeIntensity) {
      setSettings(prev => ({ ...prev, hillshadeIntensity }));
    }
  }, [hillshadeIntensity, settings.hillshadeIntensity]);

  // Handle label visibility changes from props
  useEffect(() => {
    if (isTerrainLoaded && showLabels !== settings.showLabels) {
      toggleLabels(showLabels);
    }
  }, [showLabels, settings.showLabels, isTerrainLoaded, toggleLabels]);

  return null;
};

export default Map3DTerrain;